import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProviderCard from "@/components/ProviderCard";
import NicknameCard from "@/components/NicknameCard";
import MessagesPanel from "@/components/MessagesPanel";
import MilitaryVerification from "@/components/MilitaryVerification";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CASHAPP_TAG } from "@/lib/constants";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PhoneVerifyCard } from "@/components/PhoneVerifyCard";
import { CreditCard, Star, Share2, CalendarClock, Gift, Lock, DollarSign, UserX, Home as HomeIcon, CheckCircle2, Sparkles, Camera, MessageCircle, Facebook } from "lucide-react";

function statusColor(s) {
  return { confirmed: "text-green-600", pending: "text-amber-600", cancelled: "text-red-600" }[s] || "text-slate-500";
}

function payLabel(s) {
  return { paid: "paid", unpaid: "unpaid", cashapp_pending: "Cash App — awaiting confirmation" }[s] || s;
}

export default function ClientDashboard() {
  const { user, refresh } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [favs, setFavs] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState([8]);
  const [comment, setComment] = useState("");
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [tipFor, setTipFor] = useState(null);
  const [tipAmount, setTipAmount] = useState(10);
  const [tipCashTag, setTipCashTag] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [useCredit, setUseCredit] = useState(true);
  const [quote, setQuote] = useState(null);

  const creditBalance = Number(user?.site_credit || 0);
  const dismissCelebration = async () => {
    try { await api.post("/me/celebrate-ack"); } catch { /* noop */ }
    refresh();
  };
  const openPay = (b) => {
    setPayFor(b);
    setUseCredit(true);
    setQuote(null);
    api.get(`/bookings/${b.id}/credit-quote`).then(({ data }) => setQuote(data)).catch(() => {});
  };
  const applied = quote && useCredit ? quote.applicable_credit : 0;
  const remaining = quote ? Math.round((quote.price - applied) * 100) / 100 : (payFor?.price || 0);

  useEffect(() => {
    if (tipFor?.provider_id) {
      api.get(`/providers/${tipFor.provider_id}`)
        .then(({ data }) => setTipCashTag(data.cashapp_tag || ""))
        .catch(() => setTipCashTag(""));
    } else setTipCashTag("");
  }, [tipFor]);

  const load = () => {
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/favorites").then(({ data }) => setFavs(data)).catch(() => {});
    api.get("/client/flag-calls").then(({ data }) => setFlagCalls(data)).catch(() => {});
    api.get("/recurrences").then(({ data }) => setRecurrences(data)).catch(() => {});
    api.get("/referrals").then(({ data }) => setReferrals(data)).catch(() => {});
    api.get("/qc-checks/pending").then(({ data }) => setQc(data)).catch(() => {});
    api.get("/cart").then(({ data }) => setCart(data)).catch(() => {});
  };
  useEffect(load, []);

  const [cart, setCart] = useState(null);
  const [cartBusy, setCartBusy] = useState(false);
  const removeCartItem = async (itemId) => {
    try {
      await api.delete(`/cart/${itemId}`);
      api.get("/cart").then(({ data }) => setCart(data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const checkoutCart = async () => {
    setCartBusy(true);
    try {
      const { data } = await api.post("/cart/checkout");
      if (data.created.length) {
        toast.success(`${data.created.length} booking${data.created.length === 1 ? "" : "s"} confirmed!` +
          (data.bundle_savings > 0 ? ` Bundle discount saved you $${data.bundle_savings.toFixed(2)} 🎉` : ""));
      }
      (data.failed || []).forEach((f) => toast.error(`${f.service} with ${f.provider_name}: ${f.error}`));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCartBusy(false); }
  };

  const [qc, setQc] = useState(null);
  const [qcAnswers, setQcAnswers] = useState({});
  const [qcNote, setQcNote] = useState("");
  const [qcBusy, setQcBusy] = useState(false);
  const setQcAns = (k, v) => setQcAnswers((a) => ({ ...a, [k]: v }));
  const submitQc = async (checkId) => {
    const keys = ["on_time", "dress_code", "lanyard", "nickname_greeting"];
    if (keys.some((k) => qcAnswers[k] === undefined)) {
      toast.error("Please answer all 4 questions");
      return;
    }
    setQcBusy(true);
    try {
      const { data } = await api.post(`/qc-checks/${checkId}/submit`, { ...qcAnswers, note: qcNote });
      toast.success(data.reward_granted
        ? "Quality check submitted — 5% OFF + no platform fee loaded for your next booking! 🎉"
        : "Quality check submitted — thank you for keeping our pros sharp!");
      setQcAnswers({}); setQcNote("");
      api.get("/qc-checks/pending").then(({ data: d }) => setQc(d)).catch(() => {});
      refresh && refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setQcBusy(false); }
  };

  const [referrals, setReferrals] = useState(null);
  const copyInvite = () => {
    navigator.clipboard.writeText(`${window.location.origin}/auth?ref=${referrals?.code}`);
    toast.success("Invite link copied — share it with a friend!");
  };

  const [recurrences, setRecurrences] = useState([]);
  const FREQ_LABELS = { weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };
  const cancelRecurrence = async (rid) => {
    try {
      await api.post(`/recurrences/${rid}/cancel`);
      toast.success("Recurring schedule cancelled — future visits won't be auto-booked");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [flagCalls, setFlagCalls] = useState([]);
  const [callChoice, setCallChoice] = useState({});
  const selectCall = async (reviewId) => {
    const slot = callChoice[reviewId];
    if (!slot) return toast.error("Please pick a time");
    try {
      await api.post("/reviews/flag-call/select", { review_id: reviewId, slot });
      toast.success("Call time confirmed — thank you! We'll be in touch.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [redoSlots, setRedoSlots] = useState({});
  const [redoChoice, setRedoChoice] = useState({});
  const redos = bookings.filter((b) => b.is_redo && b.status !== "cancelled");
  const upcomingSoon = bookings.filter((b) => {
    if (b.is_redo || !b.scheduled_at || b.payment_status !== "paid") return false;
    if (["completed", "cancelled"].includes(b.status)) return false;
    const ms = new Date(b.scheduled_at).getTime() - Date.now();
    return ms > 0 && ms <= 48 * 3600 * 1000;
  });
  useEffect(() => {
    redos.filter((b) => b.status === "redo_pending").forEach((b) => {
      if (redoSlots[b.id]) return;
      api.get(`/providers/${b.provider_id}/slots`).then(({ data }) => {
        const flat = [];
        (data.slots || []).forEach((d) => (d.times || []).forEach((t) =>
          flat.push({ value: `${d.date}T${t}`, label: `${d.label} · ${t}` })));
        setRedoSlots((s) => ({ ...s, [b.id]: flat }));
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);
  const scheduleRedo = async (b) => {
    const when = redoChoice[b.id];
    if (!when) return toast.error("Please pick a time slot");
    try {
      await api.post(`/bookings/${b.id}/schedule-redo`, { scheduled_at: when });
      toast.success("Your free re-do is scheduled!");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const completeRedo = async (b) => {
    try {
      await api.post(`/bookings/${b.id}/complete-redo`);
      toast.success("Thanks for confirming — glad we made it right!");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const payCredit = async (b) => {
    try {
      await api.post(`/bookings/${b.id}/pay-credit`);
      toast.success("Booking paid in full with your site credit! 🎉");
      setPayFor(null);
      refresh();
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const payCard = async (b) => {
    if (useCredit && quote && remaining <= 0) return payCredit(b);
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin, use_credit: useCredit });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const payCashApp = async (b) => {
    if (useCredit && quote && remaining <= 0) return payCredit(b);
    try {
      const { data } = await api.post(`/bookings/${b.id}/cashapp`, { use_credit: useCredit });
      window.open(data.cashapp_url, "_blank");
      toast.success(`Complete your $${data.amount} payment in Cash App to ${CASHAPP_TAG}. We'll confirm it shortly.`);
      setPayFor(null);
      refresh();
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submitReview = async () => {
    try {
      const fd = new FormData();
      fd.append("booking_id", reviewFor.id);
      fd.append("rating", rating[0]);
      fd.append("comment", comment);
      reviewPhotos.forEach((f) => fd.append("photos", f));
      await api.post("/reviews", fd);
      toast.success("Review submitted — thank you!");
      setReviewFor(null); setComment(""); setRating([8]); setReviewPhotos([]);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const shareBooking = (b) => {
    const text = `I just booked ${b.service} with ${b.provider_name} on Care-4U.net!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.origin)}`, "_blank");
  };

  const payTip = async () => {
    const amt = parseFloat(tipAmount);
    if (!amt || amt < 1) return toast.error("Enter a tip of at least $1");
    try {
      const { data } = await api.post("/tips/checkout", {
        provider_id: tipFor.provider_id, amount: amt, booking_id: tipFor.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeFav = async (id) => {
    await api.delete(`/favorites/${id}`);
    setFavs(favs.filter((f) => f.id !== id));
  };

  const flagNoShow = async (b) => {
    if (!window.confirm(`Report that ${b.provider_name} did NOT show up for your ${b.service}? This notifies the admin.`)) return;
    try {
      await api.post(`/bookings/${b.id}/no-show`);
      toast.success("No-show reported. Thank you — we take this seriously.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-10">
      {user?.paused && (
        <div className="card-metal p-4 border-2 border-slate-500 bg-slate-50" data-testid="account-paused-banner">
          <p className="font-heading font-black text-slate-800">Your booking is temporarily paused</p>
          <p className="text-sm text-slate-600 mt-1">Your account can't book new visits right now. Please reach out to Care-4U.net support so we can sort things out and get you back up and running.</p>
        </div>
      )}
      {user?.terminated && (
        <div className="card-metal p-6 border-4 border-black bg-slate-900" data-testid="terminated-banner">
          <h2 className="font-heading font-black text-xl text-white">⛔ Your Care-4U services have been terminated</h2>
          <p className="text-sm text-slate-300 mt-2">
            After coaching, another low home report was received, so this account can no longer book Care-4U services. All upcoming bookings and recurring schedules were cancelled. If you believe this was a mistake, please contact Care-4U.net support.
          </p>
        </div>
      )}
      <PhoneVerifyCard />
      {user?.hazard?.celebrate && (
        <motion.div initial={{ opacity: 0, y: -16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 220, damping: 18 }}
          className="card-metal p-6 border-2 border-green-500 bg-green-50 relative overflow-hidden" data-testid="comeback-celebration">
          <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.15, type: "spring", stiffness: 260 }}
            className="text-4xl absolute right-5 top-4 select-none">🎉</motion.div>
          <h2 className="font-heading font-black text-xl text-green-700 flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6" /> Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""} — all services unlocked!
          </h2>
          <p className="text-sm text-slate-700 mt-2">
            Your professional cleaning has been verified and your home score is reset to <b>{user?.hazard?.home_score ?? 10}/10</b>. Every Care-4U service is open to you again — we're so glad to have you back. 💛
          </p>
          <p className="text-xs text-slate-500 mt-1">Keep it spotless and you'll never see that red banner again — your pros will thank you!</p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <Link to="/search" className="btn-red-3d inline-flex !py-2 !px-4" data-testid="celebration-book-btn">
              <Sparkles className="w-4 h-4" /> Book your next visit
            </Link>
            <button onClick={dismissCelebration} className="text-sm text-slate-500 hover:text-slate-700 underline" data-testid="celebration-dismiss-btn">
              Dismiss
            </button>
          </div>
        </motion.div>
      )}
      {user?.hazard?.hazardous && (
        <div className="card-metal p-6 border-4 border-[#9b111e] bg-red-50" data-testid="hazard-home-banner">
          <div className="flex items-center gap-4">
            <img src="/hazard-home.png" alt="Hazardous home warning" className="w-16 h-16 flex-shrink-0" data-testid="hazard-home-logo" />
            <h2 className="font-heading font-black text-xl text-[#9b111e]">HAZARDOUS HOME — booking paused</h2>
          </div>
          <p className="text-sm text-slate-700 mt-3">
            More than one Care-4U pro has scored your home <b>below 7/10</b>, so your account is in <b>Hazardous Home</b> status and regular bookings are paused. Here's exactly how to get back on track:
          </p>
          <ol className="text-sm text-slate-700 mt-2 space-y-1 list-decimal list-inside">
            <li><b>Book and pay for a professional deep cleaning</b> — cleaning services are still open to you (and automatically <b>{user?.cleaning_gate?.discount_pct || 10}% off</b> 🤝).</li>
            <li>After the visit, your cleaning pro sends the administrator a <b>clean report with photos</b> of your spotless (10/10) home.</li>
            <li>The administrator verifies the photos and <b>resets your home score</b> — all services unlock.</li>
          </ol>
          <Link to="/search" data-testid="hazard-find-pro" className="btn-red-3d inline-flex mt-3 !py-2 !px-4">
            <Sparkles className="w-4 h-4" /> Book a deep cleaning
          </Link>
        </div>
      )}
      {!user?.hazard?.hazardous && user?.cleaning_gate?.cleaning_required && (
        <div className="card-metal p-6 border-2 border-[#9b111e] bg-red-50" data-testid="cleaning-gate-banner">
          <h2 className="font-heading font-black text-lg flex items-center gap-2 text-[#9b111e]">
            <Sparkles className="w-5 h-5" /> A quick reset before your next booking
          </h2>
          <p className="text-sm text-slate-700 mt-2">
            A couple of your pros let us know the home wasn't quite ready for them to work safely. To keep every Care-4U pro safe and comfortable, please <b>book and complete a Care-4U home cleaning</b> (any cleaning service) before scheduling other services again.
          </p>
          <p className="text-sm font-semibold text-emerald-700 mt-2" data-testid="cleaning-gate-discount">
            To make the reset easier, your Care-4U cleaning is automatically {user?.cleaning_gate?.discount_pct || 10}% off — on us. The discount is applied the moment you book any cleaning service. 🤝
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Once your cleaning visit is marked completed, all services unlock automatically. It's a one-time reset — thanks for helping us take care of our pros. 💛
          </p>
          <Link to="/search" data-testid="cleaning-gate-find-pro" className="btn-red-3d inline-flex mt-3 !py-2 !px-4">
            <Sparkles className="w-4 h-4" /> Find a cleaning pro
          </Link>
        </div>
      )}
      {(user?.hazard?.record?.length > 0 || user?.hazard?.final_warning) && (
        <div className="card-metal p-6 border-2 border-amber-400" data-testid="home-record-card">
          <h2 className="font-heading font-black text-lg text-slate-800">Your Home Care Record <span className="text-xs font-normal text-slate-500">(permanent)</span></h2>
          {(user?.hazard?.record || []).map((e) => (
            <p key={e.id} className="text-sm text-slate-700 mt-2" data-testid={`home-record-entry-${e.id}`}>
              📋 <b>{new Date(e.date).toLocaleDateString()}</b> — {e.note} Home score reset to <b>{e.score}/10</b>.
            </p>
          ))}
          {user?.hazard?.final_warning && (
            <p className="text-sm font-bold text-[#9b111e] mt-3 bg-red-50 border border-red-200 rounded p-3" data-testid="final-warning-note">
              ⚠ Coaching note from your administrator: your place NEEDS TO STAY clean — every pro deserves a neat, tidy, SAFE space. If we receive even one more low home report, your Care-4U services may be terminated PERMANENTLY.
            </p>
          )}
        </div>
      )}
      {user?.spotless && (user.spotless.streak > 0 || user.spotless.achieved) && (
        <div className="card-metal p-6 border border-emerald-300" data-testid="spotless-streak-card">
          <h2 className="font-heading font-black text-lg text-slate-800">✨ Spotless Streak</h2>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="spotless-progress">
            {Array.from({ length: user.spotless.target }).map((_, i) => (
              <span key={i} className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${i < Math.min(user.spotless.streak, user.spotless.target) ? "bg-emerald-500" : "bg-slate-100 grayscale opacity-40"}`}>✨</span>
            ))}
            <span className="text-sm text-slate-600 ml-2">{Math.min(user.spotless.streak, user.spotless.target)}/{user.spotless.target} spotless visits</span>
          </div>
          <p className="text-sm text-slate-600 mt-2" data-testid="spotless-streak-note">
            {user.spotless.discount_available ? (
              <>🎉 You did it! Your one-time <b>10% off</b> is loaded and applies automatically to your next booking — and pros now see your <b>✨ Spotless Home</b> badge. Thank you for taking care of your home and your pros!</>
            ) : user.spotless.achieved ? (
              <>Pros can see your <b>✨ Spotless Home</b> badge before every visit. Your one-time 10% reward has been used — thanks for keeping it spotless!</>
            ) : (
              <>Keep your home spotless — {user.spotless.target - user.spotless.streak} more visit{user.spotless.target - user.spotless.streak === 1 ? "" : "s"} earns a one-time <b>10% off</b> plus the <b>✨ Spotless Home</b> badge pros can see.</>
            )}
          </p>
        </div>
      )}
      {qc?.checks?.length > 0 && !user?.terminated && (() => {
        const check = qc.checks[0];
        const labels = {
          on_time: "Did they knock / ring the doorbell within 5 minutes of the scheduled time?",
          dress_code: "Dressed appropriately with foot covers? (collared shirt, no athletic gear, jeans or khakis, no hats)",
          lanyard: "Were they wearing their CARE-4U lanyard?",
          nickname_greeting: "Did they greet you by your nickname?",
        };
        return (
          <div className="card-metal p-5 border-2 border-slate-700" data-testid="qc-check-card">
            <h2 className="font-heading font-black text-lg text-slate-800">🔍 Random Quality Check — 30 seconds</h2>
            <p className="text-sm text-slate-600 mt-1">
              You were randomly selected! Quick check on <b>{check.provider_name}</b>'s {check.service} visit
              ({new Date(check.scheduled_at).toLocaleDateString()}). Your honest answers go straight to the administrator.
            </p>
            <p className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-2" data-testid="qc-reward-promo">
              🎁 Complete this check and get a one-time <b>5% OFF your next booking + NO platform fee</b> — applied automatically!
            </p>
            <div className="space-y-3 mt-4">
              {Object.entries(labels).map(([k, q]) => (
                <div key={k} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 p-3">
                  <p className="text-sm text-slate-700 flex-1 min-w-[200px]">{q}</p>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setQcAns(k, true)} data-testid={`qc-yes-${k}`}
                      className={`text-xs font-bold rounded-full px-4 py-1.5 border transition-colors ${qcAnswers[k] === true ? "bg-green-600 text-white border-green-700" : "bg-white text-slate-500 border-slate-300 hover:border-green-500"}`}>
                      Yes
                    </button>
                    <button onClick={() => setQcAns(k, false)} data-testid={`qc-no-${k}`}
                      className={`text-xs font-bold rounded-full px-4 py-1.5 border transition-colors ${qcAnswers[k] === false ? "bg-red-600 text-white border-red-700" : "bg-white text-slate-500 border-slate-300 hover:border-red-500"}`}>
                      No
                    </button>
                  </div>
                </div>
              ))}
              <Textarea value={qcNote} onChange={(e) => setQcNote(e.target.value)}
                placeholder="Comments for your pro? The administrator reviews them for appropriate language before they're delivered. (optional)"
                className="bg-white" data-testid="qc-note" />
              <button onClick={() => submitQc(check.id)} disabled={qcBusy} className="btn-red-3d w-full" data-testid="qc-submit-btn">
                {qcBusy ? "Submitting…" : "Submit Quality Check"}
              </button>
            </div>
          </div>
        );
      })()}
      {cart?.items?.length > 0 && !user?.terminated && (
        <div className="card-metal p-5 border-2 border-slate-300" data-testid="cart-card">
          <h2 className="font-heading font-black text-lg text-slate-800">🛒 My Booking Cart</h2>
          <p className="text-sm text-slate-600 mt-1">
            {cart.bundle_active
              ? <>Bundle unlocked! Confirm these together and <b className="text-green-700">save {cart.bundle_pct.toFixed(0)}% on every booking (~${cart.estimated_savings.toFixed(2)})</b>.</>
              : <>Add one more booking and <b>save {cart.bundle_pct.toFixed(0)}% on each</b> when you confirm them together!</>}
          </p>
          <div className="space-y-2 mt-3">
            {cart.items.map((it) => (
              <div key={it.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 p-3" data-testid={`cart-item-${it.id}`}>
                <div>
                  <p className="font-bold text-slate-800 text-sm">{it.service} <span className="font-normal text-slate-400">with {it.provider_name}</span></p>
                  <p className="text-xs text-slate-500">{new Date(it.scheduled_at).toLocaleString()} · est. ${it.price_estimate.toFixed(2)}</p>
                </div>
                <button onClick={() => removeCartItem(it.id)} className="text-xs font-bold text-red-600 hover:text-red-800 underline" data-testid={`remove-cart-item-${it.id}`}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <p className="text-sm font-bold text-slate-700" data-testid="cart-subtotal">
              Subtotal: ${cart.subtotal.toFixed(2)}
              {cart.bundle_active && <span className="text-green-700"> → ~${(cart.subtotal - cart.estimated_savings).toFixed(2)} bundled</span>}
            </p>
            <button onClick={checkoutCart} disabled={cartBusy} className="btn-red-3d !py-2 !px-5" data-testid="cart-checkout-btn">
              {cartBusy ? "Confirming…" : cart.bundle_active ? `Confirm All & Save ${cart.bundle_pct.toFixed(0)}%` : "Confirm Booking"}
            </button>
          </div>
        </div>
      )}
      {user?.qc_reward && !user?.terminated && (
        <div className="card-metal p-5 border-2 border-emerald-300 bg-emerald-50/50" data-testid="qc-reward-banner">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            <Gift className="w-5 h-5 text-emerald-600" /> 🔍 QC thank-you loaded!
          </h2>
          <p className="text-sm text-slate-700 mt-1">
            For completing your quality check: <b>{user.qc_reward.pct.toFixed(0)}% OFF your next booking + NO platform fee</b> —
            applied automatically at checkout.
            {user.qc_reward.expires_at && (() => {
              const days = Math.max(0, Math.ceil((new Date(user.qc_reward.expires_at) - Date.now()) / 86400000));
              return <b className="text-emerald-700"> ⏳ {days} day{days === 1 ? "" : "s"} left!</b>;
            })()}
          </p>
        </div>
      )}
      {user?.rebook_offer && !user?.terminated && (
        <div className="card-metal p-5 border-2 border-amber-300 bg-amber-50/50" data-testid="rebook-offer-banner">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-600" /> 🎁 Book-again reward loaded!
          </h2>
          <p className="text-sm text-slate-700 mt-1">
            Thanks for your last visit! <b>{user.rebook_offer.pct.toFixed(0)}% OFF your next booking + NO platform fee</b> —
            applied automatically at checkout. One-time use.
            {user.rebook_offer.expires_at && (() => {
              const days = Math.max(0, Math.ceil((new Date(user.rebook_offer.expires_at) - Date.now()) / 86400000));
              return <b className="text-amber-700"> ⏳ {days} day{days === 1 ? "" : "s"} left!</b>;
            })()}
          </p>
        </div>
      )}
      {user?.winback_offer && !user?.terminated && (
        <div className="card-metal p-5 border-2 border-rose-300 bg-rose-50/50" data-testid="winback-banner">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            <Gift className="w-5 h-5 text-rose-600" /> 💛 Welcome back — {user.winback_offer.pct.toFixed(0)}% off is loaded!
          </h2>
          <p className="text-sm text-slate-700 mt-1">
            We missed you! <b>{user.winback_offer.pct.toFixed(0)}% OFF your next booking</b> applies automatically — no code needed
            {user.winback_offer.expires_at && (() => {
              const days = Math.max(0, Math.ceil((new Date(user.winback_offer.expires_at) - Date.now()) / 86400000));
              return <> — <b className="text-rose-700" data-testid="winback-days-left">⏳ expires in {days} day{days === 1 ? "" : "s"}</b></>;
            })()}
            .
          </p>
        </div>
      )}
      {user?.review_offer?.status === "offered" && !user?.terminated && (
        <div className="card-metal p-5 border-2 border-amber-300 bg-amber-50/50" data-testid="offer-countdown-banner">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-600" /> {user.review_offer.pct.toFixed(0)}% off is waiting for you!
          </h2>
          <p className="text-sm text-slate-700 mt-1">
            Leave a fair review of a recent visit and <b>{user.review_offer.pct.toFixed(0)}% OFF your next booking</b> loads automatically
            {user.review_offer.expires_at && (() => {
              const days = Math.max(0, Math.ceil((new Date(user.review_offer.expires_at) - Date.now()) / 86400000));
              return <> — <b className="text-amber-700" data-testid="offer-days-left">⏳ expires in {days} day{days === 1 ? "" : "s"}</b></>;
            })()}
            . Tap <b>Review</b> on any past visit below.
          </p>
        </div>
      )}
      {user?.review_offer?.status === "earned" && !user?.terminated && (
        <div className="card-metal p-5 border-2 border-green-300 bg-green-50/50" data-testid="offer-earned-banner">
          <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
            <Gift className="w-5 h-5 text-green-600" /> 🎉 Your {user.review_offer.pct.toFixed(0)}% off is loaded!
          </h2>
          <p className="text-sm text-slate-700 mt-1">
            Thanks for your review — <b>{user.review_offer.pct.toFixed(0)}% OFF applies automatically</b> to your next booking. No code needed.
          </p>
        </div>
      )}
      {referrals && !user?.terminated && (
        <div className="card-metal p-6 border border-blue-200" data-testid="referral-card">
          <h2 className="font-heading font-black text-lg text-slate-800">🎁 Invite Friends, Earn ${referrals.credit_per_referral.toFixed(0)}</h2>
          <p className="text-sm text-slate-600 mt-1">
            Share your link — when a friend joins and completes their first booking, <b>${referrals.credit_per_referral.toFixed(2)} site credit</b> lands in your wallet automatically.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <code className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 select-all" data-testid="referral-link">
              {window.location.origin}/auth?ref={referrals.code}
            </code>
            <button onClick={copyInvite} className="btn-red-3d !py-2 !px-4 text-sm" data-testid="copy-invite-btn">
              Copy invite link
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="text-xs font-semibold text-slate-500">Share in one tap:</span>
            <a href={`sms:?&body=${encodeURIComponent(`Join me on Care-4U.net — trusted mobile pros who come to YOU! Sign up with my link and we both win: ${window.location.origin}/auth?ref=${referrals.code}`)}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-full px-3.5 py-2 transition-colors" data-testid="share-invite-sms">
              <MessageCircle className="w-3.5 h-3.5" /> Text
            </a>
            <a href={`https://wa.me/?text=${encodeURIComponent(`Join me on Care-4U.net — trusted mobile pros who come to YOU! Sign up with my link and we both win: ${window.location.origin}/auth?ref=${referrals.code}`)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#25D366] hover:bg-[#1ebe5b] rounded-full px-3.5 py-2 transition-colors" data-testid="share-invite-whatsapp">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> WhatsApp
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/auth?ref=${referrals.code}`)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#1877F2] hover:bg-[#166fe0] rounded-full px-3.5 py-2 transition-colors" data-testid="share-invite-facebook">
              <Facebook className="w-3.5 h-3.5" /> Facebook
            </a>
          </div>
          {referrals.vip && (
            <p className="text-xs font-black text-blue-700 mt-2" data-testid="referral-vip-note">
              💎 You're a Referral VIP — 10% off ALL your future bookings, applied automatically. Thank you for spreading the word!
            </p>
          )}
          {(referrals.friends.length > 0 || referrals.earned > 0) && (
            <p className="text-xs text-slate-500 mt-2" data-testid="referral-stats">
              {referrals.friends.length} friend{referrals.friends.length === 1 ? "" : "s"} joined · <b className="text-green-700">${referrals.earned.toFixed(2)} earned</b>
              {referrals.friends.filter((f) => f.status === "pending").length > 0 &&
                ` · ${referrals.friends.filter((f) => f.status === "pending").length} pending first booking`}
            </p>
          )}
        </div>
      )}
      {recurrences.filter((r) => r.active).length > 0 && (
        <div className="card-metal p-6" data-testid="recurring-card">
          <h2 className="font-heading font-black text-lg text-slate-800">🔁 Recurring Schedules</h2>
          {recurrences.filter((r) => r.active).map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 border-t border-slate-100 pt-3 mt-3" data-testid={`recurrence-${r.id}`}>
              <div>
                <p className="font-bold text-slate-800">{r.service} <span className="font-normal text-slate-500">with {r.provider_name}</span></p>
                <p className="text-sm text-slate-500">
                  🔁 {FREQ_LABELS[r.frequency] || r.frequency}
                  {r.subscribe_save && <span className="ml-2 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid={`subscribe-save-tag-${r.id}`}>Subscribe & Save · 5% off + no fee on repeat visits</span>}
                  {r.current_booking?.scheduled_at ? ` · current/next visit ${new Date(r.current_booking.scheduled_at).toLocaleString()}` : ""}
                </p>
                <p className="text-xs text-slate-400">After each completed visit, the next one is booked automatically.</p>
                {r.paused_reason && (
                  <p className="text-xs font-semibold text-amber-700 mt-1" data-testid={`recurrence-paused-${r.id}`}>⚠ Paused: {r.paused_reason}</p>
                )}
              </div>
              <button onClick={() => cancelRecurrence(r.id)} data-testid={`cancel-recurrence-${r.id}`}
                className="text-xs font-semibold text-red-600 hover:text-red-700 hover:underline whitespace-nowrap">
                Cancel series
              </button>
            </div>
          ))}
        </div>
      )}
      {flagCalls.filter((r) => r.flag?.call?.status === "offered").map((r) => (
        <div key={r.id} className="card-metal p-6 border-2 border-blue-400" data-testid={`flagcall-banner-${r.id}`}>
          <h2 className="font-heading font-black text-lg flex items-center gap-2 text-blue-700">
            <CalendarClock className="w-5 h-5" /> Care-4U wants to make things right
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            About your <b>{r.service}</b> booking — Rob would love a quick call to hear you out and offer a redo or a service credit. Please pick a time:
          </p>
          <div className="mt-3 space-y-2">
            {(r.flag.call.offered_slots || []).map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`flagcall-slot-${r.id}-${s}`}>
                <input type="radio" name={`call-${r.id}`} value={s}
                  checked={callChoice[r.id] === s}
                  onChange={() => setCallChoice((c) => ({ ...c, [r.id]: s }))} />
                {new Date(s).toLocaleString()}
              </label>
            ))}
          </div>
          <button onClick={() => selectCall(r.id)} className="btn-red-3d mt-3" data-testid={`flagcall-confirm-${r.id}`}>Confirm this time</button>
        </div>
      ))}
      {flagCalls.filter((r) => r.flag?.call?.status === "scheduled").map((r) => (
        <div key={r.id} className="card-metal p-4 border border-green-300 bg-green-50" data-testid={`flagcall-scheduled-${r.id}`}>
          <p className="text-sm text-green-800">📞 Your call about the <b>{r.service}</b> booking is confirmed for <b>{new Date(r.flag.call.selected).toLocaleString()}</b>.</p>
        </div>
      ))}
      <MilitaryVerification />
      {upcomingSoon.length > 0 && (
        <div className="card-metal p-6 border-2 border-[#9b111e]" data-testid="home-ready-card">
          <h2 className="font-heading font-black text-lg flex items-center gap-2 text-[#9b111e]">
            <HomeIcon className="w-5 h-5" /> Home Ready?
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            You've got {upcomingSoon.length === 1 ? "a visit" : `${upcomingSoon.length} visits`} coming up soon. A quick reminder of your pledge so your pro walks into a warm, safe visit:
          </p>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> Be home at the scheduled time</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> Clear a neat, tidy &amp; safe path to work — free of clutter and garbage</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> Welcome your pro warmly at the door{upcomingSoon.some((b) => b.provider_nickname) ? " by their nickname" : ""}</li>
          </ul>
          <div className="mt-3 space-y-1">
            {upcomingSoon.map((b) => (
              <p key={b.id} className="text-xs text-slate-500" data-testid={`home-ready-visit-${b.id}`}>
                <b className="text-slate-700">{b.service}</b> with {b.provider_nickname ? `${b.provider_nickname} (${b.provider_name})` : b.provider_name} · {new Date(b.scheduled_at).toLocaleString()}
              </p>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">Private reminder — only you see this. 💛</p>
        </div>
      )}
      <NicknameCard />
      {redos.length > 0 && (
        <section data-testid="redo-section">
          <h2 className="font-heading font-black text-xl mb-4 flex items-center gap-2 text-blue-700">
            <CalendarClock className="w-5 h-5" /> Free Re-Do{redos.length > 1 ? "s" : ""}
          </h2>
          <div className="space-y-3">
            {redos.map((b) => {
              const past = b.scheduled_at && new Date(b.scheduled_at).getTime() <= Date.now();
              return (
                <div key={b.id} className="card-metal p-4 border-2 border-blue-300" data-testid={`redo-${b.id}`}>
                  <p className="font-bold">{b.service} <span className="text-slate-400 font-normal">with {b.provider_name}</span>
                    <span className="ml-2 text-xs font-bold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">FREE RE-DO · $0</span>
                  </p>
                  {b.status === "redo_pending" && (
                    <div className="mt-3">
                      <p className="text-sm text-slate-600 mb-2">Pick a time from {b.provider_name}'s open slots — on us.</p>
                      <div className="flex flex-wrap gap-2 items-center">
                        <select
                          className="border border-slate-300 rounded-md p-2 text-sm bg-white max-w-xs"
                          value={redoChoice[b.id] || ""}
                          onChange={(e) => setRedoChoice((c) => ({ ...c, [b.id]: e.target.value }))}
                          data-testid={`redo-slot-select-${b.id}`}>
                          <option value="">Select a time…</option>
                          {(redoSlots[b.id] || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <button onClick={() => scheduleRedo(b)} className="btn-red-3d !py-2 !px-4" data-testid={`redo-schedule-${b.id}`}>Schedule Re-Do</button>
                      </div>
                      {(redoSlots[b.id] || []).length === 0 && <p className="text-xs text-slate-400 mt-2">No open slots in the next 30 days — check back soon.</p>}
                    </div>
                  )}
                  {b.status === "scheduled" && (
                    <div className="mt-2">
                      <p className="text-sm text-slate-600">Scheduled for <b>{new Date(b.scheduled_at).toLocaleString()}</b>.</p>
                      <button onClick={() => completeRedo(b)} disabled={!past}
                        className={`btn-silver-3d !py-2 !px-4 mt-2 ${!past ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-testid={`redo-complete-${b.id}`}>
                        <span className="text-engraved text-sm">{past ? "Confirm Completed" : "Confirm after the appointment"}</span>
                      </button>
                    </div>
                  )}
                  {b.status === "completed" && (
                    <p className="text-sm text-green-700 mt-1">✅ Completed — thanks for letting us make it right!</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
      {creditBalance > 0 && (
        <div className="card-metal p-4 flex items-center gap-3 border border-green-300 bg-green-50" data-testid="credit-balance-banner">
          <Gift className="w-6 h-6 text-green-600" />
          <div>
            <p className="font-heading font-black text-lg text-green-800" data-testid="credit-balance-amount">${creditBalance.toFixed(2)} site credit</p>
            <p className="text-sm text-green-700">Applied automatically toward your next booking. Care-4U's way of making things right.</p>
          </div>
        </div>
      )}
      <MessagesPanel />
      <section>
        <h2 className="font-heading font-black text-xl mb-4 flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-[#9b111e]" /> My Bookings
        </h2>
        {bookings.filter((b) => !b.is_redo).length === 0 && <p className="text-slate-500">No bookings yet. Find a professional to get started.</p>}
        <div className="space-y-3">
          {bookings.filter((b) => !b.is_redo).map((b) => (
            <div key={b.id} className="card-metal p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`booking-${b.id}`}>
              <div>
                <p className="font-bold">{b.service} <span className="text-slate-400 font-normal">with {b.provider_nickname ? <>{b.provider_nickname} <span className="text-slate-400">({b.provider_name})</span></> : b.provider_name}</span></p>
                {(b.provider_nickname || b.connected_since) && <p className="text-xs text-[#9b111e] font-semibold" data-testid={`nickname-greeting-${b.id}`}>You two are connected{b.provider_nickname ? ` — say hi to ${b.provider_nickname}! 😊` : ""}{b.connected_since ? ` · since ${new Date(b.connected_since).toLocaleDateString()}` : ""}</p>}
                <p className="text-sm text-slate-500">{new Date(b.scheduled_at).toLocaleString()} · ${b.price} (fee ${b.platform_fee})</p>
                {b.cleaning_discount > 0 && (
                  <p className="text-xs font-semibold text-emerald-700" data-testid={`cleaning-discount-${b.id}`}>
                    Cleaning reset discount applied — you saved ${b.cleaning_discount.toFixed(2)} 🤝
                  </p>
                )}
                {b.spotless_discount > 0 && (
                  <p className="text-xs font-semibold text-emerald-700" data-testid={`spotless-discount-${b.id}`}>
                    ✨ Spotless Streak reward — you saved ${b.spotless_discount.toFixed(2)}
                  </p>
                )}
                {b.subscribe_discount > 0 && (
                  <p className="text-xs font-semibold text-emerald-700" data-testid={`subscribe-discount-${b.id}`}>
                    🔁 Subscribe & Save — you saved ${b.subscribe_discount.toFixed(2)}
                  </p>
                )}
                {b.thanks_discount > 0 && (
                  <p className="text-xs font-semibold text-emerald-700" data-testid={`thanks-discount-${b.id}`}>
                    💝 Thank-you gift from your pro — you saved ${b.thanks_discount.toFixed(2)}
                  </p>
                )}
                {b.referral_discount > 0 && (
                  <p className="text-xs font-semibold text-blue-700" data-testid={`referral-discount-${b.id}`}>
                    💎 Referral VIP — you saved ${b.referral_discount.toFixed(2)}
                  </p>
                )}
                {b.review_offer_discount > 0 && (
                  <p className="text-xs font-semibold text-green-700" data-testid={`review-offer-discount-${b.id}`}>
                    🎁 Review reward — you saved ${b.review_offer_discount.toFixed(2)}
                  </p>
                )}
                {b.winback_discount > 0 && (
                  <p className="text-xs font-semibold text-rose-700" data-testid={`winback-discount-${b.id}`}>
                    💛 Welcome-back gift — you saved ${b.winback_discount.toFixed(2)}
                  </p>
                )}
                {b.qc_reward_discount > 0 && (
                  <p className="text-xs font-semibold text-emerald-700" data-testid={`qc-reward-discount-${b.id}`}>
                    🔍 QC thank-you — you saved ${b.qc_reward_discount.toFixed(2)} + platform fee waived
                  </p>
                )}
                {b.bundle_discount > 0 && (
                  <p className="text-xs font-semibold text-indigo-700" data-testid={`bundle-discount-${b.id}`}>
                    🛒 Bundle discount — you saved ${b.bundle_discount.toFixed(2)}
                  </p>
                )}
                {b.frequency && (
                  <p className="text-xs font-semibold text-blue-700" data-testid={`recurring-chip-${b.id}`}>
                    🔁 Repeats {(FREQ_LABELS[b.frequency] || b.frequency).toLowerCase()}
                  </p>
                )}
                <p className={`text-xs font-bold uppercase ${statusColor(b.status)}`}>{b.status} · {payLabel(b.payment_status)}</p>
              </div>
              <div className="flex gap-2">
                {b.payment_status !== "paid" && (
                  <button onClick={() => openPay(b)} data-testid={`pay-btn-${b.id}`} className="btn-red-3d !py-2 !px-4 flex items-center gap-1">
                    <CreditCard className="w-4 h-4" /> {b.payment_status === "cashapp_pending" ? "Pay Again" : "Pay"}
                  </button>
                )}
                {b.payment_status === "paid" && !b.reviewed && (
                  <button onClick={() => setReviewFor(b)} data-testid={`review-btn-${b.id}`} className="btn-silver-3d !py-2 !px-4">
                    <Star className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm">Review</span>
                  </button>
                )}
                {b.payment_status === "paid" && b.reviewed && (
                  <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1.5 self-center" data-testid={`reviewed-chip-${b.id}`}>
                    Reviewed ✓
                  </span>
                )}
                {b.payment_status === "paid" && (
                  <button onClick={() => { setTipFor(b); setTipAmount(10); }} data-testid={`tip-btn-${b.id}`} className="btn-silver-3d !py-2 !px-4">
                    <Gift className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm">Tip</span>
                  </button>
                )}
                <button onClick={() => shareBooking(b)} data-testid={`share-booking-${b.id}`} className="btn-silver-3d !py-2 !px-3">
                  <Share2 className="w-4 h-4 text-[#9b111e]" />
                </button>
                {b.no_show ? (
                  <span className="text-xs font-bold text-red-600 self-center" data-testid={`no-show-flagged-${b.id}`}>No-show reported</span>
                ) : (new Date(b.scheduled_at) < new Date() && b.status !== "cancelled") ? (
                  <button onClick={() => flagNoShow(b)} data-testid={`no-show-btn-${b.id}`} className="btn-silver-3d !py-2 !px-3" title="Report a no-show">
                    <UserX className="w-4 h-4 text-red-600" />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-heading font-black text-xl mb-4">My Favorites</h2>
        {favs.length === 0 && <p className="text-slate-500">No favorites saved yet. Tap the heart on any provider.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {favs.map((p) => (
            <ProviderCard key={p.id} provider={p} isFavorite onToggleFavorite={removeFav} />
          ))}
        </div>
      </section>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Pay ${payFor?.price} for {payFor?.service}</DialogTitle>
            <DialogDescription>Choose how you'd like to pay.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {quote && quote.available_credit > 0 && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3" data-testid="credit-panel">
                <label className="flex items-center justify-between gap-2 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm font-semibold text-green-800">
                    <Gift className="w-4 h-4" /> Use my ${quote.available_credit.toFixed(2)} site credit
                  </span>
                  <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)}
                    className="accent-green-600 w-4 h-4" data-testid="use-credit-toggle" />
                </label>
                {useCredit && (
                  <div className="mt-2 text-sm text-slate-600 flex justify-between">
                    <span>Credit applied</span>
                    <span className="font-semibold text-green-700" data-testid="credit-applied">− ${applied.toFixed(2)}</span>
                  </div>
                )}
                <div className="mt-1 text-sm text-slate-800 flex justify-between font-bold">
                  <span>You pay</span>
                  <span data-testid="remaining-due">${remaining.toFixed(2)}</span>
                </div>
              </div>
            )}
            {useCredit && quote && remaining <= 0 ? (
              <button onClick={() => payCredit(payFor)} className="btn-red-3d w-full flex items-center justify-center gap-2" data-testid="pay-credit-btn">
                <Gift className="w-4 h-4" /> Pay $0 with credit
              </button>
            ) : (
              <>
                <button onClick={() => payCard(payFor)} className="btn-red-3d w-full flex items-center justify-center gap-2" data-testid="pay-card-btn">
                  <CreditCard className="w-4 h-4" /> Pay ${remaining.toFixed(2)} with Card (Stripe)
                </button>
                <button onClick={() => payCashApp(payFor)} className="btn-silver-3d w-full flex items-center justify-center gap-2" data-testid="pay-cashapp-btn">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-engraved">Pay ${remaining.toFixed(2)} with Cash App</span>
                </button>
                <p className="text-center text-sm text-slate-500">
                  Cash App tag: <b className="text-green-600" data-testid="cashapp-tag">{CASHAPP_TAG}</b>
                </p>
              </>
            )}
            <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
              <Lock className="w-3 h-3 text-green-600" /> Secured with 256-bit encryption
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewFor} onOpenChange={(o) => { if (!o) { setReviewFor(null); setReviewPhotos([]); } }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Rate {reviewFor?.provider_name}</DialogTitle>
            <DialogDescription>Share a rating from 1 to 10, an optional comment and photos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rating: <span className="text-[#9b111e] font-black">{rating[0]}/10</span></Label>
              <Slider min={1} max={10} step={1} value={rating} onValueChange={setRating} className="mt-3" data-testid="review-rating-slider" />
            </div>
            <div>
              <Label>Comment</Label>
              <Textarea className="mt-1 bg-white" data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            <div>
              <Label>Photos <span className="text-slate-400 font-normal text-xs">(optional, up to 3 — show off that fresh cut or sparkling home!)</span></Label>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {reviewPhotos.map((f, i) => (
                  <div key={i} className="relative">
                    <img src={URL.createObjectURL(f)} alt="" className="w-16 h-16 object-cover rounded-lg border border-slate-200" data-testid={`review-photo-preview-${i}`} />
                    <button type="button" onClick={() => setReviewPhotos(reviewPhotos.filter((_, j) => j !== i))} data-testid={`remove-review-photo-${i}`}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 text-xs leading-none flex items-center justify-center">×</button>
                  </div>
                ))}
                {reviewPhotos.length < 3 && (
                  <label className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer text-slate-400 hover:border-[#9b111e] hover:text-[#9b111e] transition-colors" data-testid="add-review-photo">
                    <Camera className="w-5 h-5" />
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="review-photo-input"
                      onChange={(e) => { const files = Array.from(e.target.files || []); setReviewPhotos((p) => [...p, ...files].slice(0, 3)); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>
            <button onClick={submitReview} className="btn-red-3d w-full" data-testid="submit-review-btn">Submit Review</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tipFor} onOpenChange={(o) => !o && setTipFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Tip {tipFor?.provider_name}</DialogTitle>
            <DialogDescription>100% of your tip goes to the provider.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">100% of your tip goes directly to the provider — no platform fee.</p>
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 20, 50].map((amt) => (
                <button key={amt} onClick={() => setTipAmount(amt)} data-testid={`tip-preset-${amt}`}
                  className={`py-2 rounded-lg border font-bold transition-colors ${
                    Number(tipAmount) === amt ? "border-[#9b111e] bg-red-50 text-[#9b111e]" : "border-slate-300 text-slate-600"
                  }`}>
                  ${amt}
                </button>
              ))}
            </div>
            <div>
              <Label>Custom amount ($)</Label>
              <Input type="number" min="1" max="1000" className="mt-1 bg-white" data-testid="tip-custom"
                value={tipAmount} onChange={(e) => setTipAmount(e.target.value)} />
            </div>
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1" data-testid="secure-note-tip">
              <Lock className="w-3 h-3 text-green-600" /> Secured with <b className="mx-1">256-bit SSL encryption</b> · Payments by Stripe
            </p>
            <button onClick={payTip} className="btn-red-3d w-full" data-testid="submit-tip-btn">
              <Gift className="w-4 h-4" /> Send ${Number(tipAmount || 0).toFixed(2)} Tip
            </button>
            {tipCashTag && (
              <button
                onClick={() => window.open(`https://cash.app/${tipCashTag}/${Number(tipAmount || 0).toFixed(2)}`, "_blank")}
                className="btn-silver-3d w-full" data-testid="tip-cashapp-btn">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="text-engraved">Tip ${Number(tipAmount || 0).toFixed(2)} via Cash App ({tipCashTag})</span>
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
